import { CommonModule } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, Inject, OnDestroy, OnInit } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { environment } from '@env/environment';
import { Language } from '@app/_models';
import { OktaAuthService } from '@app/auth/services/okta-auth.service';
import { AuthState, OktaAuth } from '@okta/okta-auth-js';
import { OKTA_AUTH } from '@okta/okta-angular';
import { InactivityService } from '@app/auth/services/inactivity.service';
import { FOOTER_LEGAL_LINKS, FOOTER_SECTIONS, FOOTER_SOCIAL_LINKS } from './core/config/footer-links';
import { AppFooterComponent } from './shared/components/app-footer/app-footer.component';
import { FullScreenLoaderComponent } from './shared/components/full-screen-loader/full-screen-loader.component';
import packageInfo from '../../package.json';

const SHOW_APPLICATION_VERSION = true;

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    imports: [CommonModule, RouterModule, TranslateModule, AppFooterComponent, FullScreenLoaderComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AppComponent implements OnInit, OnDestroy {
    readonly appVersion = packageInfo.version;
    readonly showApplicationVersion = SHOW_APPLICATION_VERSION;
    readonly footerSections = FOOTER_SECTIONS;
    readonly footerLegalLinks = FOOTER_LEGAL_LINKS;
    readonly footerSocialLinks = FOOTER_SOCIAL_LINKS;

    isAuthenticated?: boolean;
    isPrivateArea = false;
    isCloseSessionDialogOpen = false;
    isLoggingOut = false;
    hideGlobalChrome = false;
    isHeaderLogoHovered = false;

    languages: Language[];
    currentLanguage: Language;
    private readonly destroy$ = new Subject<void>();

    constructor(
        @Inject(OKTA_AUTH)public oktaAuth: OktaAuth,
        private readonly translateService: TranslateService,
        private readonly router: Router,
        private readonly oktaAuthService: OktaAuthService,
        private readonly inactivityService: InactivityService
    ) {

        // Subscribe to authentication state changes
        this.oktaAuth.authStateManager.subscribe(
            (authState: AuthState) => this.isAuthenticated = authState.isAuthenticated
        );

        // set languages
        this.languages = environment.languagesList;
        this.translateService.addLangs(this.languages.map((lang: Language) => lang.code));

        // this language will be used as a fallback when a translation isn't found in the current language
        this.translateService.setDefaultLang(environment.defaultLanguage.code);

        // the lang to use, if the lang isn't available, it will use the current loader to get them
        this.currentLanguage = environment.defaultLanguage;
        this.translateService.use(environment.defaultLanguage.code);
    }

    ngOnInit(): void {
        // Get the authentication state for immediate use
        void this.initializeAuth();
        this.inactivityService.start();
        this.updatePrivateAreaState(this.router.url);
        this.router.events
            .pipe(
                filter((event): event is NavigationEnd => event instanceof NavigationEnd),
                takeUntil(this.destroy$)
            )
            .subscribe((event: NavigationEnd) => this.updatePrivateAreaState(event.urlAfterRedirects));
    }

    ngOnDestroy(): void {
        this.inactivityService.stop();
        this.destroy$.next();
        this.destroy$.complete();
    }

    // change language button handler
    onLangClick(lang: Language): void {
        this.translateService.use(lang.code);
        this.currentLanguage = lang;
    }

    onSessionButtonClick(): void {
        this.isCloseSessionDialogOpen = true;
    }

    async onHeaderClick(event: MouseEvent): Promise<void> {
        if (!this.isHeaderLogoEvent(event)) {
            return;
        }

        await this.navigateFromHeaderLogo();
    }

    async onHeaderKeyDown(event: KeyboardEvent): Promise<void> {
        if (!this.isHeaderLogoEvent(event) || !this.isActivationKey(event)) {
            return;
        }

        event.preventDefault();
        await this.navigateFromHeaderLogo();
    }

    onHeaderPointerMove(event: MouseEvent): void {
        this.isHeaderLogoHovered = this.isHeaderLogoEvent(event);
    }

    onHeaderPointerLeave(): void {
        this.isHeaderLogoHovered = false;
    }

    async onCloseSessionConfirm(): Promise<void> {
        if (this.isLoggingOut) {
            return;
        }

        this.isLoggingOut = true;
        try {
            this.inactivityService.stop();
            await this.oktaAuthService.logout();
            this.isAuthenticated = false;
            this.isPrivateArea = false;
            this.isCloseSessionDialogOpen = false;
            await this.router.navigate(['/logout-success']);
        } finally {
            this.isLoggingOut = false;
        }
    }

    onCloseSessionCancel(): void {
        this.closeSessionDialog();
    }

    onCloseSessionDialogClose(): void {
        this.closeSessionDialog();
    }

    private closeSessionDialog(): void {
        if (this.isLoggingOut) {
            return;
        }

        this.isCloseSessionDialogOpen = false;
    }

    private async initializeAuth(): Promise<void> {
        this.isAuthenticated = await this.oktaAuth.isAuthenticated();
    }

    private async navigateFromHeaderLogo(): Promise<void> {
        const isAuthenticated = await this.oktaAuth.isAuthenticated();
        this.isAuthenticated = isAuthenticated;
        await this.router.navigate([isAuthenticated ? '/main' : '/login']);
    }

    private isHeaderLogoEvent(event: MouseEvent | KeyboardEvent): boolean {
        return event.composedPath().some((target) =>
            target instanceof HTMLElement && target.classList.contains('header-logo')
        );
    }

    private isActivationKey(event: KeyboardEvent): boolean {
        return event.key === 'Enter' || event.key === ' ';
    }

    private updatePrivateAreaState(url: string): void {
        this.isPrivateArea = url.startsWith('/main');
        this.hideGlobalChrome =
            url.startsWith('/registro/identidad-validada')
            || url.startsWith('/logout-success')
            || url.startsWith('/session-expired');
    }
}
